import { DOCTOR_SEEDS } from '../registry/doctorSeeds'
import { unreviewed } from '../registry/shared'
import { DoctorEntry } from '../types'
import { PLATFORM_DOCTORS } from './platformData'

/** Review-only transcription of PRD v4 Part C; never imported by the public registry. */
const SOURCE_DOCTORS: Array<Omit<DoctorEntry, 'kind' | 'path' | 'published' | 'requiresReview' | 'review'>> = [
  {
    slug: 'carlos-seraphim',
    name: 'Carlos Eduardo Seraphim',
    honorificPrefix: 'Dr.',
    crm: {
      number: '156663',
      uf: 'SP'
    },
    specialty: 'Endocrinologia e Metabologia, com título de especialista pela SBEM',
    education: [
      'Graduação em Medicina — Faculdade de Medicina da Universidade de São Paulo (FMUSP), 2007–2012',
      'Residência em Clínica Médica — USP, 2014–2016',
      'Residência em Endocrinologia e Metabologia — Hospital das Clínicas da FMUSP, 2016–2018',
      'Doutorado em Medicina (Endocrinologia) — USP, 2019–2024. Tese: Investigação dos aspectos metabólicos da puberdade precoce central. Orientadora: Profa. Ana Claudia Latronico',
      'Pós-doutorado — USP, 2025'
    ],
    educationDetails: [
      {
        text: 'Graduação em Medicina — Faculdade de Medicina da Universidade de São Paulo (FMUSP), 2007–2012'
      },
      {
        text: 'Residência em Clínica Médica — USP, 2014–2016'
      },
      {
        text: 'Residência em Endocrinologia e Metabologia — Hospital das Clínicas da FMUSP, 2016–2018'
      },
      {
        before: 'Doutorado em Medicina (Endocrinologia) — USP, 2019–2024. Tese: ',
        text: 'Investigação dos aspectos metabólicos da puberdade precoce central',
        url: 'https://doi.org/10.11606/t.5.2024.tde-19062024-165829',
        after: '. Orientadora: Profa. Ana Claudia Latronico'
      },
      {
        text: 'Pós-doutorado — USP, 2025'
      }
    ],
    role: 'médico e revisor do conteúdo médico do site',
    experienceItems: [
      'Ex-preceptor da residência de Endocrinologia e Metabologia da USP',
      'Corpo clínico do Hospital Sírio-Libanês e do Hospital 9 de Julho',
      'Pesquisador em metabolismo, obesidade, diabetes, doenças ósseas e puberdade',
      'Membro da Endocrine Society e da Sociedade Brasileira de Endocrinologia e Metabologia (SBEM)'
    ],
    bio: 'O Dr. Carlos Seraphim é médico endocrinologista formado pela Faculdade de Medicina da USP, onde também fez residência em Clínica Médica e em Endocrinologia e Metabologia e concluiu o doutorado. Foi preceptor da residência de Endocrinologia da USP e é autor de artigos em revistas como Endocrine Reviews, The Journal of Clinical Endocrinology & Metabolism e European Journal of Endocrinology. Na Medleve, atende pacientes e revisa o conteúdo médico publicado no site.',
    publications: [
      {
        authors: 'Seraphim CE, Canton APM, Montenegro L, et al.',
        title: 'Genotype–Phenotype Correlations in Central Precocious Puberty Caused by MKRN3 Mutations',
        url: 'https://doi.org/10.1210/clinem/dgaa955',
        journal: 'The Journal of Clinical Endocrinology & Metabolism',
        year: '2021',
        note: 'Primeiro autor.'
      },
      {
        authors: 'Seraphim CE, Frassei JS, Pessoa BS, et al.',
        title:
          'Impact of Long-Term Dexamethasone Therapy on the Metabolic Profile of Patients With 21-Hydroxylase Deficiency',
        url: 'https://doi.org/10.1210/js.2019-00123',
        journal: 'Journal of the Endocrine Society',
        year: '2019',
        note: 'Primeiro autor.'
      },
      {
        authors: 'Seraphim CE, Argente J, Latronico AC.',
        title: 'Delta-like 1 homolog genetics and its emerging role in human puberty',
        url: 'https://doi.org/10.1016/j.coemr.2020.04.002',
        journal: 'Current Opinion in Endocrine and Metabolic Research',
        year: '2020',
        note: 'Primeiro autor.'
      },
      {
        authors: 'Brito VN, Canton APM, Seraphim CE, et al.',
        title: 'The Congenital and Acquired Mechanisms Implicated in the Etiology of Central Precocious Puberty',
        url: 'https://doi.org/10.1210/endrev/bnac020',
        journal: 'Endocrine Reviews',
        year: '2023'
      },
      {
        authors: 'Canton APM, Seraphim CE, Montenegro LR, et al.',
        title: 'The genetic etiology is a relevant cause of central precocious puberty',
        url: 'https://doi.org/10.1093/ejendo/lvae063',
        journal: 'European Journal of Endocrinology',
        year: '2024'
      },
      {
        authors: 'Montenegro L, Seraphim C, Tinano F, et al.',
        title:
          'Familial central precocious puberty due to DLK1 deficiency: novel genetic findings and relevance of serum DLK1 levels',
        url: 'https://doi.org/10.1093/ejendo/lvad129',
        journal: 'European Journal of Endocrinology',
        year: '2023'
      },
      {
        authors: 'Tinano FR, Canton APM, Montenegro LR, et al.',
        title: 'Clinical and Genetic Characterization of Familial Central Precocious Puberty',
        url: 'https://doi.org/10.1210/clinem/dgac763',
        journal: 'The Journal of Clinical Endocrinology & Metabolism',
        year: '2023'
      },
      {
        authors: 'Montenegro L, Labarta JI, Piovesan M, et al.',
        title:
          'Novel Genetic and Biochemical Findings of DLK1 in Children with Central Precocious Puberty: A Brazilian–Spanish Study',
        url: 'https://doi.org/10.1210/clinem/dgaa461',
        journal: 'The Journal of Clinical Endocrinology & Metabolism',
        year: '2020'
      },
      {
        authors: 'Soriano-Guillén L, Tena-Sempere M, Seraphim CE, Latronico AC, Argente J.',
        title:
          'Precocious sexual maturation: Unravelling the mechanisms of pubertal onset through clinical observations',
        url: 'https://doi.org/10.1111/jne.12979',
        journal: 'Journal of Neuroendocrinology',
        year: '2021'
      },
      {
        authors: 'Ramos CO, Canton APM, Seraphim CE, et al.',
        // Exact Crossref API title: /works/10.1515/jpem-2021-0142, retrieved 2026-10-13 (including NBSP).
        title:
          'Anthropometric, metabolic, and reproductive outcomes of patients with central precocious puberty treated with leuprorelin acetate 3-month depot (11.25 mg)',
        url: 'https://doi.org/10.1515/jpem-2021-0142',
        journal: 'Journal of Pediatric Endocrinology and Metabolism',
        year: '2021'
      },
      {
        authors: 'Ramos CO, Macedo DB, Canton APM, et al.',
        title:
          'Outcomes of Patients with Central Precocious Puberty Due to Loss-of-Function Mutations in the MKRN3 Gene after Treatment with Gonadotropin-Releasing Hormone Analog',
        url: 'https://doi.org/10.1159/000504446',
        journal: 'Neuroendocrinology',
        year: '2020'
      },
      {
        authors: 'Canton APM, Seraphim CE, Brito VN, Latronico AC.',
        title: 'Pioneering studies on monogenic central precocious puberty',
        url: 'https://doi.org/10.20945/2359-3997000000164',
        journal: 'Archives of Endocrinology and Metabolism',
        year: '2019'
      },
      {
        authors: 'Faria AG, Seraphim CE, et al.',
        title: 'Peripheral precocious puberty in girls with McCune-Albright syndrome: a case series',
        url: 'https://doi.org/10.20945/2359-4292-2024-0459',
        journal: 'Archives of Endocrinology and Metabolism',
        year: '2025'
      },
      {
        authors: 'Silveira LFG, Seraphim CE, Latronico AC.',
        title: 'Genetic Basis and Heterogeneity of Congenital Hypogonadotropic Hypogonadism',
        url: 'https://doi.org/10.1055/a-2806-2848',
        journal: 'Seminars in Reproductive Medicine',
        year: '2025'
      }
    ],
    media: [
      {
        before:
          'Veja Saúde: citado na reportagem "A febre dos hormônios: cresce uso indevido de testosterona e companhia" (Chloé Pinheiro e Pedro Nakamura, janeiro de 2025), ',
        text: 'republicada pelo Pulitzer Center',
        url: 'https://pulitzercenter.org/stories/hormone-fever-growing-misuse-testosterone-and-its-companions-portuguese',
        after: '.',
        headline: 'A febre dos hormônios: cresce uso indevido de testosterona e companhia',
        datePublished: '2025-01',
        publisher: 'Veja Saúde'
      },
      {
        before: 'CartaCapital, ',
        text: 'página de autor',
        url: 'https://www.cartacapital.com.br/author/carlosseraphim/',
        after: ':',
        publisher: 'CartaCapital'
      },
      {
        before: '',
        text: 'O uso de anabolizantes: qual o preço do "corpo perfeito"?',
        url: 'https://www.cartacapital.com.br/blogs/saudelgbt/o-uso-de-anabolizantes-qual-o-preco-do-corpo-perfeito/',
        after: ' (11/11/2019)',
        datePublished: '2019-11-11',
        publisher: 'CartaCapital'
      },
      {
        before: '',
        text: 'As mudanças nas regras para o tratamento de pessoas trans são uma vitória na medicina',
        url: 'https://www.cartacapital.com.br/blogs/saudelgbt/as-mudancas-nas-regras-para-o-tratamento-de-pessoas-trans-sao-uma-vitoria-na-medicina/',
        after: ' (13/01/2020)',
        datePublished: '2020-01-13',
        publisher: 'CartaCapital'
      }
    ],
    professionalLinks: [
      {
        before: 'Canal no YouTube: ',
        text: 'Endocrine Talks com Dr. Carlos Seraphim',
        url: 'https://www.youtube.com/@endocrinoetalks',
        after: ''
      },
      {
        before: 'Site profissional: ',
        text: 'drcarlosseraphim.com.br',
        url: 'https://www.drcarlosseraphim.com.br/sobre-o-dr-carlos-seraphim',
        after: ''
      },
      {
        before: 'Google Scholar: ',
        text: 'perfil',
        url: 'https://scholar.google.com/citations?user=cSBC7bAAAAAJ',
        after: ''
      },
      {
        before: 'LinkedIn: ',
        text: 'Carlos Eduardo Seraphim',
        url: 'https://br.linkedin.com/in/carlos-eduardo-seraphim',
        after: ''
      },
      {
        before: 'Registro profissional: ',
        text: 'Guia Médico do CREMESP',
        url: 'https://guiamedico.cremesp.org.br/',
        after: ''
      }
    ],
    sameAs: [
      'https://www.youtube.com/@endocrinoetalks',
      'https://www.drcarlosseraphim.com.br/sobre-o-dr-carlos-seraphim',
      'https://scholar.google.com/citations?user=cSBC7bAAAAAJ',
      'https://br.linkedin.com/in/carlos-eduardo-seraphim',
      'https://guiamedico.cremesp.org.br/',
      'https://www.cartacapital.com.br/author/carlosseraphim/'
    ],
    alumniOf: ['Faculdade de Medicina da Universidade de São Paulo (FMUSP)', 'USP', 'Hospital das Clínicas da FMUSP']
  },
  {
    slug: 'joao-brunhara',
    name: 'João Brunhara (João Arthur Brunhara Alves Barbosa)',
    honorificPrefix: 'Dr.',
    crm: {
      number: '161642',
      uf: 'SP'
    },
    specialty: 'Urologia.',
    education: [
      'Graduação em Medicina — Universidade de São Paulo, 2007–2013',
      'Residência em Cirurgia Geral — USP, 2014–2016',
      'Residência em Urologia — Faculdade de Medicina da USP, 2016–2019',
      "Estágio de pesquisa em cirurgia robótica urológica pediátrica — Boston Children's Hospital, Harvard Medical School, 2010–2011",
      'Formação complementar no Hôpital Henri Mondor (França)'
    ],
    educationDetails: [
      {
        text: 'Graduação em Medicina — Universidade de São Paulo, 2007–2013'
      },
      {
        text: 'Residência em Cirurgia Geral — USP, 2014–2016'
      },
      {
        text: 'Residência em Urologia — Faculdade de Medicina da USP, 2016–2019'
      },
      {
        text: "Estágio de pesquisa em cirurgia robótica urológica pediátrica — Boston Children's Hospital, Harvard Medical School, 2010–2011"
      },
      {
        text: 'Formação complementar no Hôpital Henri Mondor (França)'
      }
    ],
    publicationName: 'Barbosa JABA / Barbosa JA / Brunhara JA',
    publications: [
      {
        authors: 'Barbosa JA, Tiseo BC, Barayan GA, et al.',
        title: 'Development and Initial Validation of a Scoring System for Diagnosing Testicular Torsion in Children',
        url: 'https://www.auajournals.org/doi/abs/10.1016/j.juro.2012.10.056',
        journal: 'The Journal of Urology',
        year: '2013',
        note: 'Primeiro autor.'
      },
      {
        authors: 'Barbosa JABA, Barayan G, Gridley CM, et al.',
        title:
          'Patient and Parent Perceptions of Robotic Compared to Open Surgical Scars in Pediatric Urologic Surgery',
        url: 'https://www.sciencedirect.com/science/article/abs/pii/S0022534712060016',
        journal: 'The Journal of Urology',
        note: 'Primeiro autor.'
      },
      {
        authors: 'Barbosa JA, Kowal A, Onal B, et al.',
        title: 'Comparative evaluation of the resolution of hydronephrosis in children who underwent open and…',
        url: 'https://www.sciencedirect.com/science/article/abs/pii/S1477513112000393',
        journal: 'Journal of Pediatric Urology',
        year: '2012',
        note: 'Primeiro autor.'
      },
      {
        authors: 'De Carvalho PA, Barbosa JABA, Guglielmetti GB, et al.',
        title: 'Retrograde Release of the Neurovascular Bundle with Preservation of Dorsal Venous Complex…',
        url: 'https://www.sciencedirect.com/science/article/abs/pii/S0302283818304810',
        journal: 'European Urology',
        year: '2018'
      }
    ],
    media: [
      {
        before: 'Minha Vida: ',
        text: '11 matérias com participação do Dr. João Brunhara',
        url: 'https://www.minhavida.com.br/especialistas/39531-joao-brunhara/materias',
        after: '',
        publisher: 'Minha Vida'
      },
      {
        before: 'Blog Omens: ',
        text: 'página de autor',
        url: 'https://omens.com.br/author/dr-joao-brunhara',
        after: '',
        publisher: 'Blog Omens'
      },
      {
        before: 'Rádios EBC: ',
        text: 'Médico tira dúvidas sobre o câncer de testículos',
        url: 'https://radios.ebc.com.br/nacional-jovem/2022/11/medico-tira-duvidas-sobre-o-cancer-de-testiculos',
        after: ' (2022)',
        datePublished: '2022',
        publisher: 'Rádios EBC'
      },
      {
        before: 'CPDF: ',
        text: 'Entrevista com Dr. João Brunhara sobre dapoxetina',
        url: 'https://www.cpdf.com.br/entrevista-com-dr-joao-brunhara-sobre-dapoxetina/',
        after: '',
        publisher: 'CPDF'
      }
    ],
    professionalLinks: [
      {
        before: 'Site profissional: ',
        text: 'drjoaobrunhara.com.br — Produção científica',
        url: 'https://drjoaobrunhara.com.br/producao-cientifica/',
        after: ''
      },
      {
        before: 'Lista completa: ',
        text: 'Google Scholar',
        url: 'https://scholar.google.com.br/citations?user=bvrrvSsAAAAJ&hl=pt-BR',
        after: '.'
      }
    ],
    sameAs: [
      'https://drjoaobrunhara.com.br/producao-cientifica/',
      'https://scholar.google.com.br/citations?user=bvrrvSsAAAAJ&hl=pt-BR',
      'https://omens.com.br/author/dr-joao-brunhara'
    ],
    alumniOf: [
      'Universidade de São Paulo',
      'USP',
      'Faculdade de Medicina da USP',
      "Boston Children's Hospital",
      'Harvard Medical School',
      'Hôpital Henri Mondor (França)'
    ]
  },
  {
    slug: 'fernanda-ferreira-souza',
    name: 'Fernanda Ferreira Souza',
    honorificPrefix: 'Dra.',
    crm: {
      number: '52-0112103-0',
      uf: 'RJ'
    },
    education: [
      'Medicina (UNIRIO); residência em Medicina de família e Comunidade (SMS-Rio); residência em Administração em Saúde (UERJ); residente em Radiologia (UFRJ)'
    ],
    alumniOf: ['UNIRIO', 'SMS-Rio', 'UERJ']
  },
  {
    slug: 'caio-ariero',
    name: 'Caio Ariero',
    honorificPrefix: 'Dr.',
    crm: {
      number: '226953',
      uf: 'SP'
    },
    education: ['Medicina (USP); internato no HC-FMUSP; mais de 3.000 atendimentos pela plataforma'],
    alumniOf: ['USP', 'HC-FMUSP']
  },
  {
    slug: 'danilo-hojo-navarro',
    name: 'Danilo Hojo Navarro',
    honorificPrefix: 'Dr.',
    crm: {
      number: '176824',
      uf: 'SP'
    },
    education: [
      'Medicina e residência em Medicina de família (Santa Casa de São Paulo); preceptoria (UNA-SUS); capacitação em Lifestyle Medicine (Harvard); corpo clínico do Hospital Sírio-Libanês desde 2018'
    ],
    alumniOf: ['Santa Casa de São Paulo', 'UNA-SUS', 'Harvard']
  },
  {
    slug: 'lorraine-pacheco-carlos',
    name: 'Lorraine Pacheco Carlos',
    honorificPrefix: 'Dra.',
    crm: {
      number: '52-1373302',
      uf: 'RJ'
    },
    education: ['Pós-graduação em curso em Medicina de família e Comunidade; médica na Clínica César Pernetta (RJ)']
  },
  {
    slug: 'bruno-lopes-bridi',
    name: 'Bruno Lopes Bridi',
    honorificPrefix: 'Dr.',
    crm: {
      number: '259091',
      uf: 'SP'
    },
    education: ['Medicina (Faculdade Israelita de Ciências da Saúde Albert Einstein)'],
    alumniOf: ['Faculdade Israelita de Ciências da Saúde Albert Einstein']
  },
  {
    slug: 'daniel-dos-santos-andrade',
    name: 'Daniel dos Santos Andrade',
    honorificPrefix: 'Dr.',
    crm: {
      number: '521143239',
      uf: 'RJ'
    },
    education: [
      'Medicina (UNIRIO, 2012–2019); Graduação sanduíche (London South Bank University); Pós-graduação em Saúde Coletiva (UFBA); internista no Hospital Municipal Raul Sertã'
    ],
    alumniOf: ['UNIRIO', 'London South Bank University', 'UFBA']
  },
  {
    slug: 'erica-soares-de-oliveira',
    name: 'Érica Soares de Oliveira',
    honorificPrefix: 'Dra.',
    crm: {
      number: '51824',
      uf: 'BA'
    },
    education: ['Medicina (UNIRIO); residência em Medicina de família e Comunidade (UFOP)'],
    alumniOf: ['UNIRIO', 'UFOP']
  },
  {
    slug: 'eduarda-ribeiro-sanches',
    name: 'Eduarda Ribeiro Sanches',
    honorificPrefix: 'Dra.',
    crm: {
      number: '52-135646-1',
      uf: 'RJ'
    },
    education: ['Medicina (Universidade Estácio de Sá); Pós-graduação em curso em Saúde do Idoso (UNICV)'],
    alumniOf: ['Universidade Estácio de Sá']
  },
  {
    slug: 'leonardo-costa-soares',
    name: 'Leonardo Costa Soares',
    honorificPrefix: 'Dr.',
    crm: {
      number: '267456',
      uf: 'SP'
    },
    education: ['Medicina (Universidade São Francisco); Pós-graduação em medicina endocanabinoide (WeCann Academy)'],
    alumniOf: ['Universidade São Francisco', 'WeCann Academy']
  },
  {
    slug: 'ornella-maria-covella-resende',
    name: 'Ornella Maria Covella Resende',
    honorificPrefix: 'Dra.',
    crm: {
      number: '245328',
      uf: 'SP'
    },
    education: [
      'Medicina (Universidade Cidade de São Paulo); Pós-graduação em curso em Psiquiatria (Beneficência Portuguesa)'
    ],
    alumniOf: ['Universidade Cidade de São Paulo']
  },
  {
    slug: 'pedro-henrique-alves-da-costa',
    name: 'Pedro Henrique Alves da Costa',
    honorificPrefix: 'Dr.',
    crm: {
      number: '204593',
      uf: 'SP'
    },
    education: ['Medicina (Universidade Federal do Tocantins, 2015); residência em Clínica Médica (2019)'],
    alumniOf: ['Universidade Federal do Tocantins']
  },
  {
    slug: 'nelson-alves-da-silva-junior',
    name: 'Nelson Alves da Silva Junior',
    honorificPrefix: 'Dr.',
    crm: {
      number: '191458',
      uf: 'SP'
    },
    education: [
      'Medicina (UNIGRANRIO); residência em Saúde da família e Comunidade (SMS-Rio); Gestão de Saúde (IBCMED)'
    ],
    alumniOf: ['UNIGRANRIO', 'SMS-Rio', 'IBCMED']
  },
  {
    slug: 'maria-eduarda-da-silva-muniz',
    name: 'Maria Eduarda da Silva Muniz',
    honorificPrefix: 'Dra.',
    crm: {
      number: '273895',
      uf: 'SP'
    },
    education: ['Medicina (Faculdade Israelita Albert Einstein)'],
    alumniOf: ['Faculdade Israelita Albert Einstein']
  },
  {
    slug: 'luiza-rodrigues-de-souza',
    name: 'Luiza Rodrigues de Souza',
    honorificPrefix: 'Dra.',
    crm: {
      number: '262730',
      uf: 'SP'
    },
    education: ['Medicina (Faculdade Israelita Albert Einstein)'],
    alumniOf: ['Faculdade Israelita Albert Einstein']
  }
]

export const PART_C_DOCTORS: DoctorEntry[] = SOURCE_DOCTORS.map((source) => ({
  image: [...DOCTOR_SEEDS, ...PLATFORM_DOCTORS].find((doctor) => doctor.slug === source.slug)?.image,
  ...source,
  kind: 'doctor',
  path: `/equipe-medica/${source.slug}`,
  published: false,
  requiresReview: false,
  review: unreviewed()
}))

/** Excluded source material and missing fields. This constant is never rendered or added to JSON-LD. */
export const PENDING_DOCTOR_DATA = [
  {
    slug: 'carlos-seraphim',
    item: 'Canton APM, Tinano FR, et al. Rare variants in the MECP2 gene in girls with central precocious puberty: a translational cohort study. *The Lancet Diabetes & Endocrinology*, 2023. **Link a confirmar com o médico** (DOI não verificado).',
    reason: 'Link a confirmar com o médico; DOI não verificado.'
  },
  {
    slug: 'carlos-seraphim',
    item: 'Seraphim CE, Halpern B. Capítulo sobre os quatro pilares terapêuticos do tratamento da obesidade. In: *Obesidade na Mulher*, 3ª ed., 2019. **Título exato e editora a confirmar com o médico.**',
    reason: 'Título exato e editora a confirmar com o médico; sem link.'
  },
  {
    slug: 'carlos-seraphim',
    item: 'O Lattes lista um artigo de 2024 na *The Lancet* (inibidor da aldosterona sintase na doença renal crônica). Não foi possível confirmar se o Dr. Seraphim consta como autor ou como investigador do estudo. Não publicar até ele confirmar.',
    reason: 'Não incluir sem confirmação de autoria ou participação no estudo.'
  },
  {
    slug: 'carlos-seraphim',
    item: 'RQE 79255 e 79256',
    reason: 'Confirmar com o médico e associar cada registro à especialidade correta (B0.1; Parte D, item 5).'
  },
  {
    slug: 'carlos-seraphim',
    item: '**Veja Saúde, demais matérias:** `{{links_veja_saude: carlos-seraphim}}` — lista a fornecer pela Medleve (título, URL e data de cada matéria). Não publicar matéria sem URL.',
    reason: 'Lista de Veja Saúde a fornecer pela Medleve; placeholder sem títulos, URLs e datas.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Muracca E, Nakano E, et al. Risk Factors for Male Lower Urinary Tract Symptoms: The Role of Metabolic Syndrome and Androgenetic… *Urology*, 2013.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Muracca E, Nakano E, et al. Interactions between Lower Urinary Tract Symptoms and Cardiovascular Risk factors determine distinct… *The Journal of Urology*, 2013.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JAB, Chow JS, Benson CB, et al. Postnatal longitudinal evaluation of children diagnosed with prenatal hydronephrosis… *Prenatal Diagnosis*, 2012.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Dénes FT, Nguyen HT. Testicular Torsion—Can We Improve the Management of Acute Scrotum? *The Journal of Urology*, 2016.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Reis ST, Nunes M, et al. The Obstructed Bladder: Expression of Collagen, Matrix Metalloproteinases, Muscarinic Receptors… *Urology*, 2017.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Cury J. Androgen replacement therapy in men: current evidence and controversy. *Revista de Medicina (São Paulo)*, 2018.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Antunes AA. Minimally invasive techniques for the treatment of benign prostatic hyperplasia. *Revista de Medicina (São Paulo)*, 2018.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Arap MA. Acute scrotum: differential diagnosis and treatment. *Revista de Medicina (São Paulo)*, 2018.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Brunhara JA, Moscardi PRM, Mello MF, et al. Transperitoneal laparoscopic pyeloplasty in children: does upper urinary tract anomalies affect surgical outcomes? *International Braz J Urol*, 2018.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Srougi V, Barbosa JAB, Massaud I, et al. Predictors of complication after adrenalectomy. *International Braz J Urol*, 2019.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'Barbosa JABA, Ribeiro CTA, Grinblat B, et al. Hidradenocarcinoma of the Scrotum. *Urology*, 2019.',
    reason: 'Links a fornecer pelo médico; sem URL confirmada.'
  },
  {
    slug: 'joao-brunhara',
    item: 'RQE',
    reason: 'A fornecer pela Medleve (Parte D, item 5).'
  },
  {
    slug: 'joao-brunhara',
    item: 'Patient and Parent Perceptions of Robotic Compared to Open Surgical Scars in Pediatric Urologic Surgery — ano',
    reason: 'Ano não informado na fonte; publicação com URL mantida sem datePublished.'
  },
  {
    slug: 'joao-brunhara',
    item: '**Veja Saúde:** `{{links_veja_saude: joao-brunhara}}` — lista a fornecer pela Medleve (título, URL e data de cada matéria). Não publicar matéria sem URL.',
    reason: 'Lista de Veja Saúde a fornecer pela Medleve; placeholder sem títulos, URLs e datas.'
  },
  {
    slug: 'lorraine-pacheco-carlos',
    item: 'Graduanda em Medicina',
    reason:
      'Contradiz médica com CRM ativo. Removido; confirmar graduação e instituição antes de adicionar “Graduada em Medicina”.'
  }
] as const
